import { toast } from "@/services/toast";
import { useEffect, useState } from "react";
import { RiAddFill, RiEditFill, RiFileCopyLine, RiLink, RiPulseLine } from "react-icons/ri";
import { Link } from "react-router-dom";

import Loader from "@/components/Loader";
import Table from "@/components/Table";
import Toggle from "@/components/Toggle";
import api from "@/services/api";
import { API_URL } from "@/services/config";
import { captureError } from "@/services/error";
import useStore from "@/services/store";

const TABLE_HEADER = [
  { title: "Nom", width: "22%" },
  { title: "Diffuse des missions de", width: "22%" },
  { title: "Crée le" },
  { title: "Actions", width: "30%" },
  { title: "Actif" },
];

const Campaigns = () => {
  const { user, publisher } = useStore();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [toPublishers, setToPublishers] = useState([]);
  const [filters, setFilters] = useState({
    fromPublisherId: publisher?.id || "",
    toPublisherId: "",
    search: "",
    page: 1,
    active: true,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filters]);

  useEffect(() => {
    fetchToPublishers();
  }, [filters.fromPublisherId, filters.active]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { page, pageSize, ...searchFilters } = filters;
      const res = await api.post(`/campaign/search`, { ...searchFilters, from: (page - 1) * pageSize, size: pageSize });
      if (!res.ok) {
        throw res;
      }
      setData(res.data || []);
      setTotal(res.total || 0);
    } catch (error) {
      captureError(error, { extra: { filters } });
    } finally {
      setLoading(false);
    }
  };

  const fetchToPublishers = async () => {
    try {
      const res = await api.post(`/campaign/search`, { fromPublisherId: filters.fromPublisherId, active: filters.active, size: 100 });
      if (!res.ok) {
        throw res;
      }
      setToPublishers(
        res.data
          .map((c) => ({ id: c.toPublisherId, name: c.toPublisherName }))
          .filter((c, i, s) => s.findIndex((e) => e.id === c.id) === i)
          .sort((a, b) => (a.name || "").localeCompare(b.name)),
      );
    } catch (error) {
      captureError(error, { extra: { filters } });
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await api.post(`/campaign/${id}/duplicate`);
      if (!res.ok) {
        if (res.status === 409) {
          return toast.error("Une campagne avec ce nom existe déjà");
        } else {
          throw res;
        }
      }

      setData([res.data, ...data]);
      setTotal(total + 1);
      toast.success("Campagne dupliquée");
    } catch (error) {
      captureError(error, { extra: { id } });
    }
  };

  const handleCopy = (id) => {
    navigator.clipboard.writeText(`${API_URL}/r/campaign/${id}`);
    toast.success("Lien copié");
  };

  const handleActivate = async (value, item) => {
    try {
      const res = await api.put(`/campaign/${item.id}`, { active: value });
      if (!res.ok) {
        throw res;
      }
      setData((campaigns) => campaigns.map((c) => (c.id === res.data.id ? res.data : c)));
    } catch (error) {
      captureError(error, { extra: { item } });
    }
  };

  if (!data) {
    return <p className="p-3">Chargement...</p>;
  }

  return (
    <div className="space-y-12 p-4 sm:p-12">
      <title>API Engagement - Campagnes - Diffuser des missions</title>
      <div className="flex flex-col items-center justify-between gap-4 lg:flex-row lg:gap-32">
        <div role="search" className="flex flex-1 flex-col items-center gap-4 lg:flex-row lg:gap-4">
          <label htmlFor="campaign-search" className="sr-only">
            Chercher par nom
          </label>
          <input
            id="campaign-search"
            className="input flex-1"
            name="campaign-search"
            placeholder="Chercher par nom"
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
          />
          <label htmlFor="campaign-to-publisher" className="sr-only">
            Filtrer par annonceur
          </label>
          <select
            id="campaign-to-publisher"
            className="select flex-1"
            value={filters.toPublisherId}
            onChange={(e) => setFilters({ ...filters, toPublisherId: e.target.value, page: 1 })}
          >
            <option className="px-2" value="">
              Tous les annonceurs
            </option>
            {toPublishers.map((c, i) => (
              <option key={i} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-4">
          {user.role === "admin" && (
            <Link to="/broadcast/campaign/new" className="primary-btn flex items-center">
              Nouvelle campagne <RiAddFill className="ml-2" aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>

      <div className="flex flex-col items-center justify-between lg:flex-row">
        <p className="text-lg font-semibold" role="status" aria-live="polite" aria-atomic="true">
          {loading ? "Chargement..." : total > 1 ? `${total} campagnes` : `${total} campagne`}
        </p>

        {user.role === "admin" && (
          <div className="flex items-center">
            <Toggle
              id="toggle-show-inactive-campaigns"
              aria-label="Afficher les campagnes désactivées"
              value={!filters.active}
              onChange={(checked) => setFilters({ ...filters, active: !checked, page: 1 })}
            />
            <label className="ml-2" htmlFor="toggle-show-inactive-campaigns">
              Afficher les campagnes désactivées
            </label>
          </div>
        )}
      </div>
      {loading ? (
        <div className="flex h-full items-center justify-center">
          <Loader />
        </div>
      ) : (
        <Table
          caption="Liste des campagnes"
          header={TABLE_HEADER}
          pagination
          page={filters.page}
          pageSize={filters.pageSize}
          onPageChange={(page) => setFilters({ ...filters, page })}
          total={total}
          auto
        >
          {data.map((item, i) => (
            <tr key={i} className={`${i % 2 === 0 ? "bg-table-even" : "bg-table-odd"} table-row`}>
              <td className="px-4 py-3">
                {user.role === "admin" ? (
                  <Link to={`/broadcast/campaign/${item.id}`} className="text-blue-france break-words">
                    {item.name}
                  </Link>
                ) : (
                  <span className="break-words">{item.name}</span>
                )}
              </td>
              <td className={`px-4 py-3 ${!item.active ? "opacity-50" : "opacity-100"}`}>{item.toPublisherName}</td>
              <td className={`px-4 py-3 ${!item.active ? "opacity-50" : "opacity-100"}`}>{new Date(item.createdAt).toLocaleDateString("fr")}</td>
              <td className="px-4 py-3">
                <div className="flex w-fit gap-2 text-lg">
                  {user.role === "admin" ? (
                    <Link className="secondary-btn flex items-center" to={`/broadcast/campaign/${item.id}`}>
                      <RiEditFill className="text-lg" role="img" aria-label="Modifier la campagne" />
                    </Link>
                  ) : (
                    <button type="button" className="secondary-btn flex items-center" disabled>
                      <RiEditFill className="text-lg" role="img" aria-label="Modifier la campagne" />
                    </button>
                  )}
                  <button className="secondary-btn flex items-center" onClick={() => handleCopy(item.id)}>
                    <RiLink className="text-lg" role="img" aria-label="Copier le lien de la campagne" />
                  </button>
                  <button className="secondary-btn flex items-center" onClick={() => handleDuplicate(item.id)} disabled={user.role !== "admin"}>
                    <RiFileCopyLine className="text-lg" role="img" aria-label="Dupliquer la campagne" />
                  </button>
                  <Link className="secondary-btn flex items-center" to={`/settings/real-time?sourceId=${item.id}&sourceType=campaign`}>
                    <RiPulseLine className="text-lg" role="img" aria-label={`Voir les événements en direct de la campagne ${item.name || ""}`.trim()} />
                  </Link>
                </div>
              </td>
              <td className="px-4 py-3">
                <Toggle
                  aria-label={`${item.active ? "Désactiver" : "Activer"} la campagne ${item.name || ""}`.trim()}
                  value={item.active}
                  onChange={(v) => handleActivate(v, item)}
                  disabled={user.role !== "admin"}
                />
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
};

export default Campaigns;
